/* eslint-disable no-unused-vars */
import { X } from 'lucide-react';
import React from 'react';
import { createReleaseAppVersion, createReleaseChannel } from '../../api';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useSession } from '../../contexts/SessionContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import type { UseActionDialogReturn } from '../../hooks/useActionDialog';

type ReleaseAppVersionDialogProps = {
  actionDialogReturn: UseActionDialogReturn;
  onNavigate: (path: string, opts?: { replace?: boolean }) => void;
};

function ReleaseAppVersionDialog({ actionDialogReturn, onNavigate: _onNavigate }: ReleaseAppVersionDialogProps) {
  const {
    actionDialog,
    dialogBusy,
    dialogError,
    setActionDialog,
    setDialogBusy,
    setDialogError,
  } = actionDialogReturn;

  const { session } = useSession();
  const { toast } = useFeedback();
  const {
    workspace,
    activeOrganization,
    activeProject,
    activeReleaseApp,
    selectedAppVersions,
    setActiveReleaseAppId,
    setWorkspaceVersion,
  } = useWorkspace();

  const { organizations, apps, environments, releaseAppVersions } = workspace;

  if (!actionDialog || actionDialog.kind !== 'release-app-version') return null;

  const close = () => { if (!dialogBusy) setActionDialog(null); };

  const canSubmit =
    actionDialog.version.trim().length > 0 &&
    actionDialog.organizationId.trim().length > 0 &&
    actionDialog.projectId.trim().length > 0 &&
    actionDialog.releaseAppId.trim().length > 0;
  const selectedReleaseApp =
    workspace.releaseApps.find((ra) => ra.releaseAppId === actionDialog.releaseAppId) ??
    activeReleaseApp;
  const selectedOrganization =
    organizations.find((org) => org.id === actionDialog.organizationId) ?? activeOrganization;
  const selectedProject =
    apps.find((app) => app.id === actionDialog.projectId) ?? activeProject;
  const selectedSourceVersion = releaseAppVersions.find(
    (v) => v.releaseAppVersionId === actionDialog.sourceReleaseAppVersionId
  );

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!session || !actionDialog || actionDialog.kind !== 'release-app-version' || dialogBusy) return;
    setDialogBusy(true);
    setDialogError('');
    try {
      const releaseAppVersion = await createReleaseAppVersion(
        session.accessToken,
        actionDialog.releaseAppId,
        {
          organizationId: actionDialog.organizationId.trim(),
          projectId: actionDialog.projectId.trim(),
          releaseAppId: actionDialog.releaseAppId.trim(),
          version: actionDialog.version.trim(),
          sourceReleaseAppVersionId:
            actionDialog.sourceReleaseAppVersionId.trim() || undefined
        }
      );
      setActiveReleaseAppId(releaseAppVersion.releaseAppId);
      const otaEnvs = ['dev', 'staging', 'production', 'internal'] as const;
      await Promise.all(
        environments
          .filter((env) => (otaEnvs as readonly string[]).includes(env.slug))
          .map((env) =>
            createReleaseChannel(session.accessToken, {
              organizationId: actionDialog.organizationId.trim(),
              projectId: actionDialog.projectId.trim(),
              tenantEnvironmentId: env.tenantEnvironmentId,
              environment: env.slug as typeof otaEnvs[number],
              channel: releaseAppVersion.version,
              isActive: true
            }).catch(() => null)
          )
      );
      setWorkspaceVersion((v) => v + 1);
      toast('success', 'App version added');
      setActionDialog(null);
    } catch (err: any) {
      setDialogError(err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.');
    } finally {
      setDialogBusy(false);
    }
  }

  return (
    <div className="dialogOverlay" role="presentation" onClick={close}>
      <form className="dialogPanel" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="dialogHeader">
          <div>
            <div className="panelKicker">App store versions</div>
            <h3>New version</h3>
            <p>Creating a version automatically generates the delivery lanes.</p>
          </div>
          <button type="button" className="iconButton" onClick={close} aria-label="Close dialog">
            <X size={16} />
          </button>
        </div>
        <div className="settingsForm">
          <div className="settingsSplit">
            <label className="settingsField">
              <span>Organization</span>
              <input value={selectedOrganization?.name || 'Selected organization'} disabled />
            </label>
            <label className="settingsField">
              <span>Project</span>
              <input value={selectedProject?.name || 'Selected project'} disabled />
            </label>
          </div>
          <label className="settingsField">
            <span>Release app</span>
            <input
              value={
                selectedReleaseApp
                  ? `${selectedReleaseApp.name} · ${selectedReleaseApp.platform.toUpperCase()}`
                  : 'Selected app store track'
              }
              disabled
            />
          </label>
          <label className="settingsField">
            <span>Version</span>
            <input
              value={actionDialog.version}
              onChange={(e) => setActionDialog({ ...actionDialog, version: e.target.value })}
              placeholder="1.0.0"
              autoFocus
            />
            <p className="fieldHint">This version becomes the seed for generated lanes.</p>
          </label>
          <label className="settingsField">
            <span>Clone from existing version</span>
            <select
              value={actionDialog.sourceReleaseAppVersionId}
              onChange={(e) => setActionDialog({ ...actionDialog, sourceReleaseAppVersionId: e.target.value })}
            >
              <option value="">Start clean</option>
              {selectedAppVersions.map((version) => (
                <option key={version.releaseAppVersionId} value={version.releaseAppVersionId}>
                  {version.version}
                </option>
              ))}
            </select>
            <p className="fieldHint">Copy lane defaults from a previous version.</p>
          </label>
          {selectedSourceVersion ? (
            <div className="summaryPill">
              Cloning from {selectedSourceVersion.version} · {selectedAppVersions.length} available versions
            </div>
          ) : null}
        </div>
        {dialogError && <p className="fieldError">{dialogError}</p>}
        <div className="dialogActions">
          <button type="button" className="secondaryButton" onClick={close} disabled={dialogBusy}>Cancel</button>
          <button type="submit" className="primaryButton" disabled={dialogBusy || !canSubmit}>
            {dialogBusy ? 'Creating…' : 'Create version'}
          </button>
        </div>
      </form>
    </div>
  );
}

export { ReleaseAppVersionDialog };
